import { authFetch } from "./authClient";
import { ensureFreshStoredAuth } from "./auth";
import { expireAuthSession } from "./authStore";
import type { UserServerRow, UserQueryRow, UserVaultEntryRow } from "./userDataTypes";

// ── Pull (D1 → local) ───────────────────────────────────────────────

export interface UserDataSnapshot {
  servers: UserServerRow[];
  queries: UserQueryRow[];
  vaultEntries: UserVaultEntryRow[];
}

export async function pullAllUserData(): Promise<UserDataSnapshot> {
  const [serversRes, vaultRes] = await Promise.all([
    authFetch("/user/servers"),
    authFetch("/user/vault"),
  ]);

  if (!serversRes.ok) throw new Error("Failed to fetch servers");
  if (!vaultRes.ok) throw new Error("Failed to fetch vault");

  const { servers } = (await serversRes.json()) as { servers: UserServerRow[] };
  const { entries: vaultEntries } = (await vaultRes.json()) as { entries: UserVaultEntryRow[] };

  // Fetch queries for all servers in parallel
  const queryResults = await Promise.all(
    servers.map(async (server) => {
      const res = await authFetch(`/user/servers/${server.id}/queries`);
      if (!res.ok) return [];
      const { queries } = (await res.json()) as { queries: UserQueryRow[] };
      return queries;
    }),
  );
  const queries = queryResults.flat();

  return { servers, queries, vaultEntries };
}

// ── Push (local → D1) ───────────────────────────────────────────────

export async function pushServer(server: UserServerRow): Promise<void> {
  const res = await authFetch(`/user/servers/${server.id}`, {
    method: "PUT",
    body: JSON.stringify(server),
  });
  if (!res.ok) throw new Error("Failed to push server");
}

export async function pushQuery(query: UserQueryRow): Promise<void> {
  const res = await authFetch(`/user/queries/${query.id}`, {
    method: "PUT",
    body: JSON.stringify(query),
  });
  if (!res.ok) throw new Error("Failed to push query");
}

export async function pushVaultEntry(entry: UserVaultEntryRow): Promise<void> {
  const res = await authFetch(`/user/vault/${entry.id}`, {
    method: "PUT",
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error("Failed to push vault entry");
}

export async function deleteRemoteServer(serverId: string): Promise<void> {
  await authFetch(`/user/servers/${serverId}`, { method: "DELETE" });
}

export async function deleteRemoteQuery(queryId: string): Promise<void> {
  await authFetch(`/user/queries/${queryId}`, { method: "DELETE" });
}

export async function deleteRemoteVaultEntry(entryId: string): Promise<void> {
  await authFetch(`/user/vault/${entryId}`, { method: "DELETE" });
}

// ── Bulk push (offline → online reconciliation) ─────────────────────

export async function bulkPush(data: {
  servers: UserServerRow[];
  queries: UserQueryRow[];
  vaultEntries: UserVaultEntryRow[];
}): Promise<UserDataSnapshot> {
  const res = await authFetch("/user/sync/push", {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to bulk push");
  return res.json() as Promise<UserDataSnapshot>;
}

// ── SSE sync stream ─────────────────────────────────────────────────

export interface SyncStreamEvent {
  type: "server-changed" | "query-changed" | "vault-changed" | "server-deleted" | "query-deleted" | "vault-deleted" | "ready";
  data: unknown;
}

export function openUserSyncStream(
  onEvent: (event: SyncStreamEvent) => void,
): { close: () => void; ready: Promise<void> } {
  const controller = new AbortController();
  let closed = false;

  const API_BASE = (
    (import.meta.env.VITE_SHARED_CONFIG_API_BASE as string | undefined)
    ?? "https://spuerhund-api.cloudeflare-8a5.workers.dev/api"
  ).replace(/\/$/, "");

  const ready = (async () => {
    const auth = await ensureFreshStoredAuth();

    const response = await fetch(`${API_BASE}/user/sync/stream`, {
      headers: { Authorization: `Bearer ${auth.accessToken}` },
      signal: controller.signal,
    });

    if (response.status === 401) {
      expireAuthSession();
      throw new Error("Session expired");
    }
    if (!response.ok) throw new Error(`Sync stream failed: HTTP ${response.status}`);
    if (!response.body) throw new Error("Sync stream not available");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (!closed) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      for (;;) {
        const boundary = buffer.indexOf("\n\n");
        if (boundary === -1) break;
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const parsed = parseSseEvent(chunk);
        if (parsed) onEvent(parsed);
      }
    }
  })();

  return {
    close() {
      if (closed) return;
      closed = true;
      controller.abort();
    },
    ready,
  };
}

function parseSseEvent(chunk: string): SyncStreamEvent | null {
  const lines = chunk.split(/\r?\n/);
  let eventName = "";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }

  if (!eventName || dataLines.length === 0) return null;
  const data = JSON.parse(dataLines.join("\n"));
  return { type: eventName as SyncStreamEvent["type"], data };
}
